import { Component, type ErrorInfo, type ReactNode } from 'react';

import ErrorState from '@/components/ErrorState';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  error: Error | null;
};

/**
 * Última linha de defesa: sem ela, um erro de render deixa a tela **em branco**, sem
 * nada que diga ao usuário o que houve nem como sair.
 *
 * Continua sendo classe porque React não oferece equivalente em hooks.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Ponto de integração com monitoramento (Sentry e afins) quando houver.
    console.error('Erro não tratado na árvore React:', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <ErrorState
        title="Algo deu errado"
        description="Não foi possível exibir esta tela. Recarregue a página; se o problema continuar, avise a equipe responsável."
        retryLabel="Recarregar"
        onRetry={() => window.location.reload()}
      />
    );
  }
}
